import type {
  SystemTimeData,
  SystemTimeResponse,
} from '../../models/system-time.model';

/** Unix seconds are 10 digits; milliseconds are 13. */
const UNIX_MS_THRESHOLD = 1e12;

function unwrapSystemData(payload: unknown): SystemTimeData | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const root = payload as SystemTimeResponse & SystemTimeData;
  if (root.data && typeof root.data === 'object') {
    return root.data;
  }

  if (root.system || root.user) {
    return root;
  }

  return null;
}

export function parseSystemTimestamp(payload: unknown): number | null {
  const timestamp = unwrapSystemData(payload)?.system?.timestamp;
  if (
    typeof timestamp !== 'number' ||
    !Number.isFinite(timestamp) ||
    timestamp <= 0
  ) {
    return null;
  }

  return timestamp < UNIX_MS_THRESHOLD ? timestamp * 1000 : timestamp;
}

export function parseSystemUserTimezone(payload: unknown): string | null {
  const timezone = unwrapSystemData(payload)?.user?.timezone;
  if (typeof timezone !== 'string') {
    return null;
  }

  const trimmed = timezone.trim();
  return trimmed ? trimmed : null;
}

export function computeClockOffsetMs(
  serverTimestampMs: number,
  localEndMs: number,
  localStartMs: number
): number {
  const rtt = Math.max(0, localEndMs - localStartMs);
  return serverTimestampMs + rtt / 2 - localEndMs;
}

export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
}
